import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { FileSpreadsheet, FileText, Loader2, Search } from 'lucide-react';
import dayjs from 'dayjs';
import { OrgReportsApi, OrgTeacherAttendanceReportRow } from '@/services/org-reports.api';
import { OrgPortalApi } from '@/services/org.api';
import type { ExportFormat } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Input, Select } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';

const columnHelper = createColumnHelper<OrgTeacherAttendanceReportRow>();

const COLUMNS = [
  columnHelper.accessor('teacherName', { header: 'Name' }),
  columnHelper.accessor('schoolName', { header: 'School' }),
  columnHelper.accessor('workingDays', { header: 'Working Days' }),
  columnHelper.accessor('totalPresent', { header: 'Present', cell: (ctx) => <Badge tone="green">{ctx.getValue()}</Badge> }),
  columnHelper.accessor('totalAbsent', { header: 'Absent', cell: (ctx) => <Badge tone="red">{ctx.getValue()}</Badge> }),
  columnHelper.accessor('percentage', { header: '%', cell: (ctx) => `${ctx.getValue()}%` }),
];

/**
 * Teacher Attendance Report — org tier, read-only. Flat per-teacher
 * working_days/present/absent/percentage over a date range, matching the
 * real `state-reports.teacher-attendance-report` shape. `working_days`
 * is per-person here (see OrgReportsService.collectTeacherAttendance()'s
 * doc comment) — genuinely different from the sibling Student Attendance
 * Report's per-section working_days.
 */
export function OrgTeacherAttendanceReportPage() {
  const [schoolId, setSchoolId] = useState('');
  const [stateId, setStateId] = useState<number | ''>('');
  const [startDate, setStartDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [busy, setBusy] = useState<ExportFormat | null>(null);
  const dt = useDataTable({ defaultLimit: 20 });

  useEffect(() => {
    dt.onPaginationChange((prev) => ({ ...prev, pageIndex: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, stateId, startDate, endDate]);

  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-org-teacher-attendance-report'],
    queryFn: async () => (await OrgPortalApi.listSchools({ limit: 100 })).items,
  });

  const { data: states = [] } = useQuery({
    queryKey: ['org-report-states'],
    queryFn: () => OrgReportsApi.listStates(),
  });

  const filterParams = {
    schoolId: schoolId || undefined,
    stateId: stateId === '' ? undefined : stateId,
    startDate,
    endDate,
  };

  const { data, isFetching } = useQuery({
    queryKey: ['org-teacher-attendance-report', dt.params, filterParams],
    queryFn: () => OrgReportsApi.teacherAttendanceReport({ ...dt.params, ...filterParams }),
    placeholderData: keepPreviousData,
  });

  const runExport = async (format: ExportFormat) => {
    if (busy) return;
    setBusy(format);
    try {
      await OrgReportsApi.exportTeacherAttendanceReport(format, { ...filterParams, search: dt.params.search });
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Teacher Attendance Report"
        description="Per-teacher working days, present, absent, and attendance percentage, across your organization's schools."
        actions={
          <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('xlsx')}
              disabled={!!busy}
              title="Export to Excel"
            >
              {busy === 'xlsx' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-green-600" />}
              Excel
            </button>
            <div className="w-px bg-slate-200" />
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('pdf')}
              disabled={!!busy}
              title="Export to PDF"
            >
              {busy === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-red-600" />}
              PDF
            </button>
          </div>
        }
      />

      {data?.truncated && (
        <div className="mb-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
          This organization has more teachers than this report can show at once — results are truncated.
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            value={dt.searchInput}
            onChange={(e) => dt.setSearchInput(e.target.value)}
            placeholder="Search by name"
            className="w-64 pl-9"
          />
        </div>
        <Select className="!w-56" value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
          <option value="">All schools</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <Select
          className="!w-48"
          value={stateId}
          onChange={(e) => setStateId(e.target.value === '' ? '' : Number(e.target.value))}
        >
          <option value="">All states</option>
          {states.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <Input type="date" className="!w-40" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        <span className="text-sm text-slate-400">to</span>
        <Input type="date" className="!w-40" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
      </div>

      <DataTable
        columns={COLUMNS}
        result={data}
        state={dt}
        loading={isFetching}
        emptyMessage="No teachers found."
        getRowId={(row) => `${row.schoolId}:${row.teacherId}`}
      />
    </>
  );
}
